import React, { useEffect, useState } from "react"

import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@coval/ui"
import { RotateCcw } from "lucide-react"

import { VersionHistoryPanel } from "@/components/version-history/VersionHistoryPanel"

import {
  buildCurrentDisplay,
  buildVersionDisplay,
} from "../utils/testSetVersionDisplay"
import { TestSetVersionGrid } from "./TestSetVersionGrid"
import { useTestSetVersionHistory } from "./TestSetVersionHistoryContext"

/**
 * Read-only version-history layout: the selected version's test-case rows render in the main
 * column as a read-only grid (the version-history counterpart of the editor grid), and a docked
 * right panel lists the current test set plus each prior version for navigation. Reverting to a
 * prior version mints a new REVERT version server-side, preserving the current state in history.
 */
export const TestSetVersionHistoryView: React.FC = () => {
  const {
    currentRecord,
    currentRows,
    versions,
    isLoading,
    isError,
    selectedKey,
    selectVersion,
    close,
    revertToVersion,
    isReverting,
  } = useTestSetVersionHistory()
  const [isConfirmingRevert, setIsConfirmingRevert] = useState(false)
  const [revertError, setRevertError] = useState<string | null>(null)

  useEffect(() => {
    setIsConfirmingRevert(false)
    setRevertError(null)
  }, [selectedKey])

  const currentDisplay = currentRecord
    ? buildCurrentDisplay(currentRecord, currentRows)
    : null
  const versionDisplays = versions.map(buildVersionDisplay)
  const allDisplays = currentDisplay
    ? [currentDisplay, ...versionDisplays]
    : versionDisplays
  const selectedDisplay =
    allDisplays.find((display) => display.key === selectedKey) ?? currentDisplay

  const handleRevert = async () => {
    if (!selectedDisplay || selectedDisplay.isCurrent) {
      return
    }
    setRevertError(null)
    try {
      await revertToVersion(selectedDisplay.key)
      close()
    } catch {
      setRevertError("Couldn’t revert to this version. Please try again.")
    }
  }

  const handleRevertDialogOpenChange = (open: boolean) => {
    if (isReverting) {
      return
    }
    setIsConfirmingRevert(open)
    if (!open) {
      setRevertError(null)
    }
  }

  return (
    <div className="flex h-full w-full bg-surface-contrast">
      <div className="flex-1 overflow-y-auto px-6 py-6">
        <div className="flex flex-col gap-6">
          {selectedDisplay ? (
            <>
              <div className="flex items-start justify-between gap-4">
                <div className="flex flex-col gap-1">
                  <span className="text-sm font-medium text-coval-text">
                    {selectedDisplay.title}
                  </span>
                  <span className="text-xs text-coval-text-muted">
                    Read-only · {selectedDisplay.subtitle}
                  </span>
                </div>
                {!selectedDisplay.isCurrent ? (
                  <Button
                    variant="secondary"
                    disabled={isReverting}
                    onClick={() => setIsConfirmingRevert(true)}
                  >
                    <RotateCcw className="h-4 w-4" />
                    Revert to this version
                  </Button>
                ) : null}
              </div>

              <TestSetVersionGrid rows={selectedDisplay.rows} />
            </>
          ) : (
            <p className="text-sm text-coval-text-muted">Loading version…</p>
          )}
        </div>
      </div>

      <VersionHistoryPanel
        currentItem={currentDisplay}
        versions={versionDisplays}
        selectedKey={selectedKey}
        onSelect={selectVersion}
        onClose={close}
        isLoading={isLoading}
        isError={isError}
        emptyMessage="No previous versions yet. Editing this test set’s configuration will create one."
      />

      <Dialog
        open={isConfirmingRevert}
        onOpenChange={handleRevertDialogOpenChange}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Revert to {selectedDisplay?.title ?? "this version"}?
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className="text-coval-text-muted">
              This replaces the current test set’s configuration and test-case
              rows with this version. The current state is kept in history.
            </p>
            {revertError ? (
              <p className="mt-2 text-sm text-coval-danger-text">
                {revertError}
              </p>
            ) : null}
          </DialogBody>
          <DialogFooter>
            <Button
              variant="secondary"
              disabled={isReverting}
              onClick={() => setIsConfirmingRevert(false)}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={isReverting}
              onClick={() => {
                void handleRevert()
              }}
            >
              {isReverting ? "Reverting…" : "Confirm revert"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
